<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="max-container flex flex-col gap-x-8 xl:gap-x-32">
    <div class="w-full md:w-2/3">
      <h2 :id="header?.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')">
        {{ header }}
      </h2>
      <div
        v-if="bodyCopy"
        class="body-1 mb-8"
        v-html="bodyCopy"
      />
    </div>
    <SdsTabs
      v-model="topicTabs"
      type="underline"
      size="sm"
      variant="red"
    >
      <template
        v-for="tab in topicTabs"
        :key="`${tab.key}-tab`"
        #[`tab(${tab.key})`]
      >
        {{ tab.title }}
      </template>
      <template
        v-for="tab in topicTabs as Tab[]"
        :key="`${tab.key}-panel`"
        #[`panel(${tab.key})`]
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-8 user-select-none"
        >
          <div
            v-for="topic in tab.topics"
            :key="topic.title"
            data-id="card-block"
            class="h-full group col-span-1 rounded-none bg-gray-25 hover:bg-gray-50 focus:bg-gray-50 active:bg-gray-100 focus:outline-hidden no-underline text-black"
          >
            <div class="h-full flex flex-col">
              <a
                :href="topic.url"
                target="_blank"
                tabindex="-1"
                rel="noopener noreferrer"
                class="peer/image"
              >
                <FocalImage
                  :image="{ url: topic.hero }"
                  img-class="aspect-video"
                />
              </a>
              <div class="h-full flex flex-col p-8 peer-hover/image:[&>a]:text-red-600 peer-hover/image:[&>a]:no-underline peer-hover/image:[&>a>svg]:translate-x-2!">
                <a
                  :href="topic.url"
                  tabindex="0"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="heading-4 mb-2 peer underline hover:no-underline"
                >
                  {{ topic.title }}
                </a>
                <div
                  class="body-2 overflow-ellipsis"
                  :class="topic.featured_projects.length ? 'line-clamp-3 min-h-fit' : ''"
                >
                  {{ topic.description }}
                </div>
                <h3
                  v-if="topic.featured_projects.length"
                  class="heading-6 mt-4 mb-2 text-gray-800"
                >
                  Featured Projects
                </h3>
                <ul
                  v-if="topic.featured_projects.length"
                >
                  <li
                    v-for="project in topic.featured_projects || []"
                    :key="project.title"
                    class="body-2 mb-2 list-disc marker:text-red-600 ml-4"
                  >
                    <a
                      :href="project.url"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="underline hover:text-red-600 focus:text-red-600 active:text-red-800"
                    >
                      {{ project.title }}
                    </a>
                  </li>
                </ul>
                <a
                  :href="topic.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  tabindex="-1"
                  class="mt-auto peer-focus:[&>svg]:translate-x-2! peer-hover:[&>svg]:translate-x-2!"
                >
                  <FaIcon
                    name="arrow-right"
                    icon-style="regular"
                    icon-size="lg"
                    class="text-red-600 mt-8 md:mt-16"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </template>
    </SdsTabs>
  </div>
</template>

<script setup lang="ts">
  interface FeaturedProject {
    title: string,
    url: string
  }

  interface Topic {
    title: string,
    description: string,
    hero: string,
    url: string,
    featured_projects?: FeaturedProject[]
  }

  interface Tab {
    active?: boolean,
    key: string,
    // count: number, // uncomment to add count badges to tabs (needs to be re-enabled in the context data as well)
    title: string,
    topics: Topic[]
  }

  interface ResearchTopics {
    uuid?: string,
    header?: string,
    bodyCopy?: string,
    tabs?: Tab[]
  }

  const props = withDefaults(defineProps<ResearchTopics>(), {
    uuid: '',
    header: '',
    bodyCopy: '',
    tabs: () => []
  });

  const topicTabs = ref([]);

  const rootElement = ref(null);

  onBeforeMount(() => {
    topicTabs.value = props.tabs
    rootElement.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement);
    delete rootElement.value.dataset['json']
  });

  onMounted(() => {
    /* Use an anchor link in the URL to open to a specific tab */
    props.tabs.forEach(tab => {
      if (tab.key === window.location.href.split('#')[1]) {
        topicTabs.value = topicTabs.value.map(tab => tab.key === window.location.href.split('#')[1] ? { ...tab, active: true } : { ...tab, active: false });
      }
    })
  })
</script>
